import { Scheduler } from './scheduler';
import { EventBus } from './events';
import { IMPULSE_STONE, Pantheon } from './god';
import { CultureSystem } from './culture';
import { costOf, processViability, graspCost, graspDamage } from './leverage';
import { StrainField, effectiveMixAt } from '@/world/strainField';
import { StrainReads } from '@/world/strainReads';
import {
  GRAIN, PLANT_HOSTS, STRAIN_CELL, drawStrain, isOrdinary, newStrainMix, strainOf,
  type StrainHost,
} from '@/world/strain';
import { AnchorSystem, ANCHOR_CEILING, ANCHOR_COST, ANCHOR_REACH, POWER_PER_DAY,
  type AnchorState } from './anchor';
import { AuraSystem, type AuraVerb } from './aura';
import { Chronicle } from './chronicle';
import { EarthStore, earthCost, lensVolume, ampForVolume, minThrow,
         REPOSE } from '@/world/earth';
import { applyShape, readGeology, shapeLand, type GeoReading } from '@/world/geology';
import { FireSim } from './fire';
import { LightningSystem } from './lightning';
import { HabitatSampler } from './social/habitat';
import { SOCIAL_CELL_SIZE, socialCenter, toSocial } from './social/socialGrid';
import { PRESS_HALFLIFE, PRESS_PER_CAST, WildlifeSystem } from './wildlife/populations';
import type { BeastProcess, WildlifeSpeciesId } from './wildlife/types';
import { BandSystem } from './mortals/band';
import { SCAR_CELL, Overlay } from '@/world/overlay';
import { EnvField } from '@/world/env';
import { riversOf } from '@/world/rivers';
import { ThingStore, type ThingKind } from '@/world/things';
import { SereStore } from '@/world/sere';
import { CarriedStore } from '@/world/carried';
import { litterAt, litterMass, litterAttachment, LIVING, KIND_NAME,
  type LitterKind } from '@/world/litter';
import { relicNear, relicMass, relicAttachment, RELIC_KINDS, type RelicKind }
  from '@/world/relics';
import { wornAt } from '@/world/wear';
import type { Region } from '@/world/region';
import { BIOMES } from '@/world/types';
import { SNAPSHOT_VERSION, type SimulationSnapshot } from '@/sim/persistence/snapshot';
import { clamp, hash01 } from '@/core/rng';

/**
 * The whole living world behind one call: advance(from, to).
 *
 * Systems run on the shared fixed-grid scheduler in the spec's canonical
 * order — fire before wildlife before bands — so a day of catch-up and a
 * day of live play produce the same history. Rendering sits outside: it
 * reads this state each frame and owns none of it.
 */

/**
 * What each verb is, as an event and as a physical stamp. The values are the
 * verb at *reference focus* — the 420 m disc every downstream tuning was
 * calibrated against. Magnitude is the verb's strength in its own units;
 * salience is how loudly it demands explanation.
 */
/**
 * How much has to burn before it is history.
 *
 * Read off the distribution rather than chosen (`tools/burn.ts`, twelve
 * god-free years): episode sizes come out
 * `1×28, 2×6, then 4, 19, 41, 207, 509, 1202, 3054, 11273` — bimodal, with the
 * gap exactly between two cells and four. **81% of all ignitions burn two
 * cells or fewer**, and a 32 m patch that went out in six minutes is not
 * something the world should remember for ever.
 *
 * At three cells this keeps 8 episodes of 42 — **0.7 marks a world-year**,
 * about one memorable fire every eighteen months.
 */
export const BURN_MARK_CELLS = 3;

/**
 * How hard a full-work channelled cast presses the strain field, before
 * viability and the charge scale it — the wake's one new verb constant.
 *
 * **STATUS: DESIGN NUMBER, written down to be measured against** (the
 * CLAUDE.md discipline): a tap at ordinary viability moves the stand about a
 * quarter of the way toward its variety, so establishment (SETTLE_THRESHOLD
 * 0.6) is three or four tended casts and then eight quiet years — a
 * session-scale project with a history-scale payoff, law 29 at all three
 * horizons. `tools/strain.ts §3` is the probe that re-derives it against the
 * measured cast rate, and it can run now that the sim reads a strain.
 */
const TILT_GAIN = 0.35;
/** Scratch for the act's strain resolution; nothing survives a call. */
const pressMix = newStrainMix(GRAIN.mixMax + 1);

const DIVINE_VERBS = {
  strike: { type: 'divine_strike', salience: 0.9, magnitude: 1, moisture: 0, biomass: 0 },
  rain: { type: 'divine_rain', salience: 0.55, magnitude: 0.7, moisture: 0.7, biomass: 0 },
  strengthen: { type: 'divine_strengthen', salience: 0.5, magnitude: 0.6, moisture: 0.1, biomass: 0.6 },
  weaken: { type: 'divine_weaken', salience: 0.7, magnitude: 0.7, moisture: -0.25, biomass: -0.7 },
  /**
   * Omen writes nothing into the land at all — see act(), which returns
   * before the stamp. It is the informational verb: the only thing it
   * changes is where mortals are looking.
   */
  omen: { type: 'divine_omen', salience: 0.8, magnitude: 1, moisture: 0, biomass: 0 },
} as const;


/* ── Divine Focus (v0.3.2 §50.4–5) ──────────────────────────────────────────
 *
 * Focus is the scale of divine attention. The scroll wheel chooses a radius;
 * the band table names it; the radius gives it a concentration. Four gestures
 * and no sliders: movement is where the deity is, look is where attention is
 * aimed, scroll is how wide, verb is intent. Flying up to cross a mountain
 * must not turn Rain into a watershed miracle — which is why Focus is chosen,
 * never inferred from where the camera happens to be. Reach still gates the
 * widest choosable focus (see focusFor below), so progression stays physical.
 *
 * Simulation still cannot see the camera or the wheel — main.ts resolves a
 * focus here and hands the *result* to act(). The sim only ever receives two
 * numbers.
 */

export type FocusBandName = 'presence' | 'local' | 'grove' | 'valley' | 'watershed';

/** What act() needs to know about the player's attention. */
export interface DivineFocus {
  /** True cast radius in metres. */
  radius: number;
  /** Strength per unit area; 1 at the reference cast. */
  concentration: number;
}

/** A focus plus what the chrome and the reticle need to draw it. */
export interface Focus extends DivineFocus {
  band: FocusBandName;
  /** 0 crisp and defined .. 1 wide and diffuse. Render-only. */
  softness: number;
}

/**
 * The reference cast: 420 m at concentration 1 — the fixed disc every verb
 * was tuned against before focus existed. act() falls back to it, so any
 * caller that does not care about focus gets exactly the old behaviour.
 */
export const FOCUS_REFERENCE: DivineFocus = { radius: 420, concentration: 1 };

/**
 * Tuned bands, not raw proportion. Each maps a range of camera distances onto
 * a range of cast radii, log-interpolated inside the band, so a band feels
 * like a *register* of attention rather than a continuous dial: one tree, one
 * clearing, one grove, one valley, one watershed.
 */
export const FOCUS_BANDS: ReadonlyArray<{
  name: FocusBandName; cam: readonly [number, number]; cast: readonly [number, number];
}> = [
  { name: 'presence', cam: [8, 60], cast: [5, 20] },
  { name: 'local', cam: [60, 200], cast: [20, 100] },
  { name: 'grove', cam: [200, 700], cast: [100, 400] },
  { name: 'valley', cam: [700, 2600], cast: [400, 1500] },
  { name: 'watershed', cam: [2600, 60000], cast: [2000, 6000] },
];

/**
 * Concentration falls off with area, but *sub*-linearly: `power / area^0.55`,
 * which on radius is an exponent of 1.1. Strict inverse-area (exponent 2)
 * would make a watershed cast 1/200th as strong as a grove cast and therefore
 * pointless; at 0.55 the total delivered effect — concentration × area —
 * still grows as `area^0.45`, so casting wide is the most *total* power a god
 * can spend, just the thinnest.
 *
 * The law runs bare and exact between two guard clamps, which are the only
 * tuning in it:
 *  · a ceiling, because the bare law at 5 m is 130× the reference and every
 *    verb saturates the land's ±1 deviation long before that — past the
 *    ceiling a tighter cast buys precision, not more force;
 *  · a floor, because a watershed cast that cannot be seen is a cast nobody
 *    will ever spend. This is the "regional casts must not be useless" rule,
 *    written as a number.
 *
 * Between them — 138 m to 2.0 km, which is Grove and Valley, where the game
 * is actually played — the exponent is exactly 0.55.
 */
const CONC_EXP = 1.1;                 // 2 × 0.55 — the law, expressed on radius
const CONC_MIN = 0.18, CONC_MAX = 3.4;

/**
 * The apron a verb channelled into a thing throws around it.
 *
 * `APRON_MIN` is a hair under the 90 m the design describes; the floor
 * matters because a channelled cast snaps to one 32 m cell and three times
 * that is still only a clearing. `APRON_SHARE` is what the surrounding ground
 * gets relative to the thing itself — a third, so the target stays plainly the
 * strongest point and the halo reads as spilling outward from it rather than
 * as a second, wider cast. Priced against `tools/radiate.ts`.
 */
const APRON_MIN = 72, APRON_MAX = 140, APRON_SHARE = 0.34;

export function concentrationFor(radius: number): number {
  const raw = Math.pow(FOCUS_REFERENCE.radius / Math.max(1, radius), CONC_EXP);
  return clamp(raw, CONC_MIN, CONC_MAX);
}

/** How diffuse the attention looks: crisp below 60 m, fully soft past 4 km. */
export const softnessFor = (radius: number): number =>
  clamp(Math.log(Math.max(radius, 60) / 60) / Math.log(4000 / 60), 0, 1);

/**
 * Salience shifts gently with reach — a whisper over one tree is not the
 * event a storm over the watershed is — and is exactly 1× at the reference,
 * so a focus-less cast keeps its historical salience to the digit.
 */
const SALIENCE_SPAN = Math.log(6000 / 5);
const salienceScale = (radius: number): number =>
  clamp(1 + 0.42 * Math.log(Math.max(1, radius) / FOCUS_REFERENCE.radius) / SALIENCE_SPAN,
        0.6, 1.35);

/**
 * The focus of an explicitly *chosen* radius — the scroll wheel's entrypoint.
 *
 * Focus used to be derived from the orbit camera's distance; now movement is
 * free flight and the scroll wheel owns scale directly, so this is the map
 * the game actually plays through. The band table stays the authority: a
 * radius inside a band keeps it, and a radius in the deliberate gap between
 * Valley (≤1500 m) and Watershed (≥2000 m) snaps to the nearer edge by log
 * distance — a watershed act starts at two kilometres or it is not one.
 */
export function focusForRadius(chosen: number): Focus {
  const bands = FOCUS_BANDS;
  const r0 = clamp(chosen, bands[0].cast[0], bands[bands.length - 1].cast[1]);
  let band = bands[bands.length - 1];
  for (let i = 0; i < bands.length; i++) {
    const b = bands[i];
    if (r0 > b.cast[1]) continue;
    if (r0 >= b.cast[0]) { band = b; break; }
    // in the gap below b: the nearer edge wins, measured in ratios
    const prev = bands[i - 1];
    band = r0 < Math.sqrt(prev.cast[1] * b.cast[0]) ? prev : b;
    break;
  }
  const radius = clamp(r0, band.cast[0], band.cast[1]);
  return {
    band: band.name,
    radius,
    concentration: concentrationFor(radius),
    softness: softnessFor(radius),
  };
}

/**
 * The focus the old orbit camera paid at this distance. Free flight no longer
 * derives Focus from the camera, but this map is still the law for what reach
 * *permits*: focusFor(maxAltitude).radius is the widest focus a god of that
 * reach may choose, which keeps progression exactly where the altitude
 * ceiling used to put it.
 */
export function focusFor(camDistance: number): Focus {
  const cam = Math.max(1, camDistance);
  let def = FOCUS_BANDS[FOCUS_BANDS.length - 1];
  for (const b of FOCUS_BANDS) { if (cam <= b.cam[1]) { def = b; break; } }
  const t = clamp(Math.log(cam / def.cam[0]) / Math.log(def.cam[1] / def.cam[0]), 0, 1);
  const radius = def.cast[0] * Math.pow(def.cast[1] / def.cast[0], t);
  return {
    band: def.name,
    radius,
    concentration: concentrationFor(radius),
    softness: softnessFor(radius),
  };
}

/**
 * Strike alone does not scale by getting bigger — a god's power made wide
 * becomes *less precise*, not more explosive. Below ~620 m a strike is one
 * bolt, exactly where it was aimed. Wider, and it becomes an electrical storm
 * inside the footprint: more bolts, scattered, most of them somewhere you did
 * not choose.
 */
const BOLT_UNIT = 620, BOLT_EXP = 1.05, BOLT_MAX = 16;

export const boltCountFor = (radius: number): number =>
  Math.min(BOLT_MAX, 1 + Math.floor(Math.pow(Math.max(1, radius) / BOLT_UNIT, BOLT_EXP)));

/**
 * Where the bolts fall. Pure: world seed, aim, radius and world-time in,
 * the same storm out, forever. The first bolt is always the aim itself — a
 * god who wanted one place still gets one bolt there — and the rest walk a
 * golden-angle spiral, hash-jittered, spread evenly by area over the disc.
 */
export function strikeSites(
  seed: number, x: number, z: number, radius: number, now: number,
): Array<{ x: number; z: number }> {
  const n = boltCountFor(radius);
  const out = [{ x, z }];
  if (n <= 1) return out;
  const qx = Math.round(x), qz = Math.round(z);
  const salt = (seed ^ Math.imul(Math.round(now), 0x9e3779b1)) | 0;
  for (let i = 1; i < n; i++) {
    const ha = hash01(qx + i, qz - i, salt);
    const hr = hash01(qz - i, qx + i, salt ^ 0x5bf03635);
    const ang = i * 2.399963229728653 + (ha - 0.5) * 1.1;
    const rad = radius * Math.min(0.97, Math.sqrt((i + 0.3 + hr * 0.55) / n));
    out.push({ x: x + Math.cos(ang) * rad, z: z + Math.sin(ang) * rad });
  }
  return out;
}

/** Where a bolt fell, and whether the land took it. */
export interface DivineBolt { x: number; z: number; lit: boolean }

/** How far from the Presence a god may reach to take hold of something. */
export const GRASP_REACH = 60;

/** What came of taking hold of something. */
export interface GraspResult {
  ok: boolean;
  eventId?: string;
  /** What it took out of the god, 0..1. */
  cost?: number;
  /** Roughly what the thing weighed, kg — the reason it cost what it did. */
  mass?: number;
  /** 0..1 of rough handling done to it. */
  damage?: number;
  /** The thing's identity — its baseline place. */
  key?: string;
  reason?: 'nothing-there' | 'too-weak' | 'too-far' | 'no-such-god';
}

export interface DivineResult {
  ok: boolean;
  eventId?: string;
  /** What it took out of the god, 0..1 — Design III §86. */
  cost?: number;
  /** Why it did not happen, when it did not. */
  reason?: 'too-weak' | 'no-such-god';
  /** The radius the act actually used — the same number the event carries. */
  radius: number;
  concentration: number;
  /** Strike only: every bolt of the storm, in the order they were resolved. */
  bolts?: DivineBolt[];
  /**
   * A cast aimed at a person: how many people it actually leaned on.
   *
   * Present and **zero** when the aim was a person and nobody in the footprint
   * had anything being expressed — which is a real answer and not a failure,
   * and the chrome is expected to say so. Absent when the cast was not aimed
   * at a person at all, so the caller can tell "nothing to lean on" from "not
   * that kind of act".
   */
  touched?: number;
  /**
   * Whether THE PERSON YOU AIMED AT moved, as opposed to anybody near them.
   *
   * Parties cluster at a camp, so a cast on somebody sitting by the fire lands
   * on the dozens around them who are out on work. `touched` alone would then
   * report a healthy number for a person who moved not at all, and the chrome
   * would credit the god with something that did not happen to the subject
   * they named.
   */
  named?: boolean;
  /**
   * A cast that reached the WILD — aimed at a herd by a god whose Godhood
   * lets Strengthen mean the herd's own drive (`beast.press.*`), or cast wide
   * by one whose Godhood lets it lean on every inclined herd in the disc.
   *
   * `process` is what the press landed on and `delivered` how much of one
   * whole press it was; `process: null` with `delivered: 0` is the honest
   * refusal — the herd was not doing anything the god could lean on (not
   * inclined to move, not gathering, no prey) — and the chrome is expected to
   * say so, exactly as `touched: 0` does for a person. Absent when the cast
   * was not the kind that could reach the wild at all.
   */
  beast?: { process: BeastProcess | null; delivered: number; herds: number };
}

export type { BeastProcess } from './wildlife/types';

/** The kinds a wide press walks — the land species, in the ledger's own order. */
const LAND_KINDS: readonly WildlifeSpeciesId[] = ['grazer', 'browser', 'small_game', 'predator', 'scavenger'];

export class Simulation {
  /**
   * Every god acting on this world, the player among them and not above
   * them (v0.3.2 §deity identity). It lives in the simulation because a
   * save must restore *who was acting*, not only what was done — and
   * because the server will one day own exactly this.
   */
  readonly pantheon = new Pantheon();
  readonly events = new EventBus();
  readonly env: EnvField;
  readonly overlay: Overlay;
  /**
   * The things this world has made important (§4). Separate from `env`
   * because it is a different *kind* of memory: env holds conditions, which
   * decay in weeks, and this holds status, which decays in centuries. A
   * blessed tree that stops being blessed the moment the ground dries out
   * was the hole this fills.
   */
  readonly things: ThingStore;
  /** Objects a god has physically moved (Design IV §21). */
  readonly carried: CarriedStore;
  readonly fire: FireSim;
  /**
   * What burned ground remembers, for decades — succession's whole state
   * (§52). Beside `overlay` because they are the same event on two clocks:
   * the char heals in forty days, the stand takes eighty years.
   */
  readonly sere: SereStore;
  /** The sky's own ignition — stateless, so it rides in no snapshot. */
  readonly lightning: LightningSystem;
  readonly habitat: HabitatSampler;
  readonly wildlife: WildlifeSystem;
  readonly bands: BandSystem;
  /** Places gods have bound themselves to by coming down and standing there. */
  readonly anchors: AnchorSystem;
  /** Which peoples these are — separate from band, place, religion, polity. */
  readonly cultures: CultureSystem;
  /**
   * The other half of law 31: what a god leaves burning at its own stones.
   * Manual compresses causality, aura accumulates it — see `sim/aura.ts`.
   */
  readonly auras: AuraSystem;
  /** Earth a god has taken hold of and moved — `world/earth.ts`. */
  readonly earth = new EarthStore();
  /**
   * Ground whose plant/animal mixture has been pushed off its baseline —
   * `world/strainField.ts`, woken 2026-08-19. The ecology reads it through
   * `strainReads` (the habitat's forage and fuel, the fire's spread, the
   * herds' worth and wariness), the fire writes it (a burn cuts and then
   * selects), the daily walk runs its two history gates, and a channelled
   * Strengthen or Weaken on a hosted plant presses it. Law 10 holds by
   * construction: no cell carries a hand.
   */
  readonly strains: StrainField;
  readonly strainReads: StrainReads;
  /**
   * The durable record — what gods actually did, and what the world durably
   * became (`sim/chronicle.ts`). Beside `events` and emphatically not instead
   * of it: the ring is PERCEPTION, spanning a measured 0.470 world-years and
   * 99.6% herd movement, and this is MEMORY, folded and forgetful in the way
   * law 2 wants. `D §6.2`'s history qualification reads this one.
   */
  readonly chronicle = new Chronicle();

  private readonly scheduler = new Scheduler(60);
  private readonly dirty = new Set<string>();
  /** See the `mods` option: identity when nobody has invested in anything. */
  private modOf: (deityId: string, variable: string) => number;
  /** See `useGodhood`: no god holds any rule until a Web says so. */
  private ruleOf: (deityId: string, variable: string) => boolean = () => false;

  /**
   * Hand the world a Godhood reader after construction.
   *
   * Needed because the ordering runs the other way in the app: the Simulation
   * exists at boot and the Web is loaded from storage after it, and
   * `fromSnapshot` builds a fresh Simulation on every load. A constructor-only
   * option would silently leave every adopted world un-Godhooded, which is the
   * quietest possible way for this whole feature to stop working.
   *
   * `rules` is the second, boolean-shaped channel — `GodhoodWeb.hasRule` per
   * deity. It exists because the number-shaped `mod()` CANNOT carry a rule:
   * its guard rewrites any non-positive to 1, so `false` reads as on. The
   * Beasts direction is mostly rules — what a verb may MEAN on a herd — and
   * `act` reads them through `rule()` at the one place a verb decides what it
   * lands on. Absent, no god holds any rule and the world is exactly as it
   * was, which is the same property that made `mod` safe to wire early.
   */
  useGodhood(
    fn: (deityId: string, variable: string) => number,
    rules?: (deityId: string, variable: string) => boolean,
  ): void {
    this.modOf = fn;
    if (rules) this.ruleOf = rules;
  }

  /**
   * What this god's Godhood has made of one variable. 1 means untouched.
   *
   * Deliberately a method rather than a cached table: a player spends points
   * mid-session and the world has to answer with the god they are NOW, and the
   * read is a short walk over the active effects rather than anything that
   * would want caching.
   */
  mod(deityId: string, variable: string): number {
    const m = this.modOf(deityId, variable);
    return Number.isFinite(m) && m > 0 ? m : 1;
  }

  /** Whether this god's Godhood holds a rule. False for anything it cannot vouch for. */
  rule(deityId: string, variable: string): boolean {
    return this.ruleOf(deityId, variable) === true;
  }

  constructor(
    readonly region: Region,
    readonly wind: { x: number; z: number },
    opts: { env?: EnvField; overlay?: Overlay; things?: ThingStore;
            sere?: SereStore; strains?: StrainField;
            /**
             * What a god's Godhood has done to a named simulation variable —
             * `GodhoodWeb.modifier`, injected rather than imported.
             *
             * This is the seam `sim/godhood/index.ts` was written for and then
             * had no caller for: 48 variables are bound to real code, 160 are
             * asked for by the Web's nodes, and `modifier()` had **zero call
             * sites in the whole build**. So a player could earn levels, spend
             * points and change nothing about how their god plays — the
             * progression loop ran act → consequence → Chronicle → level →
             * points → *nothing*.
             *
             * Injected, for three reasons. `src/sim/` must not reach into the
             * UI's localStorage (law 7 — build as though the server owns
             * history). It keeps the Web out of the snapshot for now, which is
             * what avoids a `SNAPSHOT_VERSION` bump and the 20-minute
             * prehistory rebuild behind it. And it takes a `deityId`, so a
             * rival god can have a different build the day one exists rather
             * than the day this is refactored.
             *
             * Absent, every variable reads 1 and the world behaves exactly as
             * it did — which is the property `modifier()`'s own docstring
             * names as what makes it safe to wire early.
             */
            mods?: (deityId: string, variable: string) => number } = {},
  ) {
    this.modOf = opts.mods ?? (() => 1);
    this.env = opts.env ?? new EnvField();
    this.overlay = opts.overlay ?? new Overlay();
    this.things = opts.things ?? new ThingStore();
    this.sere = opts.sere ?? new SereStore();
    this.strains = opts.strains ?? new StrainField(region);
    this.strainReads = new StrainReads(region, this.strains);
    this.carried = new CarriedStore();
    this.fire = new FireSim(region, this.overlay, this.env, this.sere,
                            this.strainReads, this.strains);
    this.lightning = new LightningSystem(
      region.seed, region.extentMetres / 2, this.fire, this.events);
    this.habitat = new HabitatSampler(region, this.env, this.overlay, this.strainReads);
    this.wildlife = new WildlifeSystem(region.seed, this.habitat, this.events);
    // the herds read the sere (ROADMAP §42): without this line the season, the
    // range, `fled`, the two crossings and `stirring()` are live but no herd
    // reads a burn's grass — handed in after construction so `fromSnapshot`,
    // which constructs and then restores, is covered too. Idempotent.
    this.wildlife.useSere(this.sere);
    this.wildlife.useStrains(this.strainReads);
    this.anchors = new AnchorSystem(this.events, (id, v) => this.mod(id, v));
    this.cultures = new CultureSystem(region.seed);
    // The aura reads the same ground a cast does — climate wetness, what the
    // biome can carry, and what the land already remembers — so the leverage
    // law applies to patience exactly as it applies to a gesture.
    this.auras = new AuraSystem(
      {
        soil: (x, z) => {
          const s = this.region.siteAt(x, z);
          return { rain: s.rain, carrying: BIOMES[s.biome]?.density ?? 0.4 };
        },
        deviation: (x, z, now) => this.env.at(x, z, now),
        stamp: (x, z, r, d, at) => this.env.stamp(x, z, r, d, at),
      },
      () => this.anchors.all(),
      (id) => this.pantheon.get(id),
      this.events,
      (id, v) => this.mod(id, v),
    );
    this.bands = new BandSystem({
      anchors: this.anchors,
      cultures: this.cultures,
      seed: region.seed,
      habitat: this.habitat,
      wildlife: this.wildlife,
      events: this.events,
      land: this.env,
      // danger is the fire that is actually burning, not an abstraction
      hazards: { dangerAt: (x, z) => this.fireDanger(x, z) },
      // ground that can carry a structure: dry by HEIGHT — the bicubic test
      // the water plane itself uses, never the 260 m land mask, which
      // `tools/shore.ts` measured miscalling 13.95 km² of real headland —
      // and not inside a river. Construction only asks this for its far
      // placement stations, a few times per band-year.
      buildable: (x, z) =>
        this.region.heightAt(x, z) > 0 && !(riversOf(this.region).at(x, z)?.inWater),
    });

    // witnessing (spec Phase A): every emission reaches the bands the moment
    // it happens — memory forms at the event, not at the day's bookkeeping.
    // The Chronicle folds the same emission first: it writes nothing any band
    // can read, so the order is free, and taking it first means the journal
    // sees an event even if witnessing later throws.
    this.events.onEmit((e) => {
      this.chronicle.record(e);
      this.bands.witness(e);
    });

    // spec §52 ordering: sky → fire → wildlife → bands, then the slow
    // bookkeeping. Lightning registers ahead of the fire tick so a bolt's
    // cell is alight before the same boundary's spread step runs.
    this.scheduler.add(60, this.lightning);
    this.scheduler.add(60, {
      advance: (_f, t) => {
        for (const k of this.fire.tick(t, this.wind)) this.dirty.add(k);
        // …and a burn that finished becomes one durable mark rather than ten
        // thousand cells. `fire_burned` was declared in events.ts and emitted
        // nowhere from the day it was written, which cost the Chronicle its
        // tenth kind of consequence (ROADMAP §28).
        for (const b of this.fire.drainBurns()) {
          if (b.cells < BURN_MARK_CELLS) continue;
          this.events.emit({
            type: 'fire_burned', time: t, x: b.x, z: b.z,
            radius: b.radius,
            // in the unit the world already counts fire in
            magnitude: (b.cells * SCAR_CELL * SCAR_CELL) / 1e4,
            salience: clamp(0.45 + Math.log10(b.cells) * 0.16, 0, 1),
            cause: { kind: 'natural' },
            ...(b.lit.length ? { parentEventIds: [...b.lit] } : {}),
            // `magnitude` already IS the hectares; a rounded copy here read
            // "0 ha" for a four-cell burn, which is a lie the record does not
            // need to carry twice
            data: { cells: b.cells, minutes: Math.round((b.to - b.from) / 60) },
          });
        }
      },
    });
    this.scheduler.add(86400, this.wildlife);
    this.scheduler.add(3600, this.bands);
    // gods, on the day grid: power comes back slowly, and the ground a god
    // once attended forgets it over centuries rather than seasons
    this.scheduler.add(86400, {
      advance: (f, t) => {
        for (const g of this.pantheon.all()) {
          // `impulse.regen` — how fast a god comes back. Per deity, because
          // this loop already runs over the whole pantheon and a rival god
          // with its own build should recover at its own rate.
          g.power = clamp(g.power + POWER_PER_DAY * this.mod(g.id, 'impulse.regen'), 0, 1);
        }
        this.anchors.decay(f, t);
        // the strains' two history gates: what has held long enough settles,
        // what has been settled and isolated long enough becomes a daughter.
        // Daily is the honest cadence — the gates read absolute spans, so any
        // grid reaches the same state, and the store is sparse.
        {
          const h = this.strains.history(t);
          for (const k of h.dirty) this.dirty.add(k);
        }
        // …and then the god pays, out of the same day's income, for whatever
        // it left burning. A god that has overcommitted guts its own auras
        // here rather than going into debt (law 31, priced in tools/aura.ts).
        for (const k of this.auras.advance(f, t)) this.dirty.add(k);
        // and the slowest clock in the world: what a people believes,
        // changing by itself over years rather than seasons
        this.cultures.drift(f, t);
        // Promotion decays on a twenty-five-year half-life, so a day is the
        // finest grid at which a sacred thing's standing visibly moves — and
        // the day it drops under the threshold, the ground has to be rebuilt
        // without its mark. The store is sparse, so this is a handful of keys.
        /**
         * …and the god stops being paid for it. A promotion that has run out
         * is the world withdrawing a confirmation it once gave, and until now
         * nothing noticed: `Strand.enduredAt` was set once and never unset,
         * while `standingOf`'s persistence term GROWS with age — so a god's
         * level went on rising, faster every year, off a tree that had been an
         * ordinary tree for a century (`WIRING.md` A13).
         *
         * BEFORE the sweep, deliberately: `sweep` deletes marks that have
         * fallen under `NEGLIGIBLE`, and a mark deleted is a mark whose lapse
         * can no longer be computed. Ordering, not preference.
         *
         * The span is half-open on the absolute grid, and the crossing instant
         * is analytic, so each lapse is reported exactly once however the
         * advance was sliced.
         */
        for (const key of this.things.lapsedBetween(f, t)) this.chronicle.lapsed(key, t);
        for (const k of this.things.sweep(t)) this.dirty.add(k);
        if (this.things.size > 0) for (const k of this.things.chunks()) this.dirty.add(k);
        // and the slowest ground-clock of all: a stand crossing a stage
        // boundary regrows a height class, and recovered ground rejoins the
        // baseline by being deleted from the store
        for (const k of this.sere.sweep(t)) this.dirty.add(k);
        // the journal forgets what came to nothing, on the same principle and
        // the same grid: a god's history is BASELINE + SPARSE HISTORY too
        this.chronicle.sweep(t);
      },
    });
    this.scheduler.add(1800, {
      advance: (_f, t) => {
        for (const k of this.overlay.healedSweep(t)) this.dirty.add(k);
        for (const k of this.strains.sweep(t)) this.dirty.add(k);
        if (this.overlay.size > 0) for (const k of this.overlay.scarChunks()) this.dirty.add(k);
        for (const k of this.env.sweep(t)) this.dirty.add(k);
        if (this.env.size > 0) for (const k of this.env.chunks()) this.dirty.add(k);
      },
    });
  }

  /** Stock the island and found the first band near a promising spot. */
  populate(now: number, near: { x: number; z: number }): { x: number; z: number } {
    const half = this.region.extentMetres / 2;
    const { sx: s0x, sz: s0z } = toSocial(-half, -half);
    const { sx: s1x, sz: s1z } = toSocial(half, half);
    this.wildlife.seedPopulations({ sx0: s0x, sz0: s0z, sx1: s1x, sz1: s1z }, now);

    // the most settleable cell within a day's walk of the given point
    let best = { x: near.x, z: near.z, s: -1 };
    const { sx, sz } = toSocial(near.x, near.z);
    for (let dz = -3; dz <= 3; dz++) for (let dx = -3; dx <= 3; dx++) {
      const h = this.habitat.at(sx + dx, sz + dz, now);
      if (h.settlementSuitability > best.s) {
        best = { x: (sx + dx + 0.5) * 1024, z: (sz + dz + 0.5) * 1024, s: h.settlementSuitability };
      }
    }
    this.bands.spawn(best.x, best.z, 25, now);
    return best;
  }

  advance(from: number, to: number): void {
    this.scheduler.advance(from, to);
  }

  /**
   * Bind a god to a place (v0.3.2 §Anchor) — the one act that is not a
   * gesture at a distance.
   *
   * Everything else a deity does it does from wherever it happens to be.
   * This it must attend: the presence has to be standing at the spot and
   * low over it, which is why the caller passes where the god actually *is*
   * rather than only where it is pointing. It costs most of a month's power
   * and it grants nothing — no followers, no claim, no territory. Rival
   * gods may anchor the same hillside, and a contested place is a feature.
   *
   * Refusals are honest and named, because a god that silently fails to make
   * the rarest thing in the game would be a bug the player could not see.
   */
  anchorAt(
    x: number, z: number, now: number,
    presence: { x: number; y: number; z: number; ground: number },
    deityId: string = this.pantheon.player.id,
  ): { ok: boolean; reason?: 'too-far' | 'too-high' | 'too-weak' | 'unknown-god';
       anchor?: AnchorState } {
    const god = this.pantheon.get(deityId);
    if (!god) return { ok: false, reason: 'unknown-god' };
    if (Math.hypot(presence.x - x, presence.z - z) > ANCHOR_REACH) {
      return { ok: false, reason: 'too-far' };
    }
    if (presence.y - presence.ground > ANCHOR_CEILING) {
      return { ok: false, reason: 'too-high' };
    }
    if (god.power < ANCHOR_COST) return { ok: false, reason: 'too-weak' };
    god.power = clamp(god.power - ANCHOR_COST, 0, 1);
    return { ok: true, anchor: this.anchors.place(deityId, x, z, now) };
  }

  /**
   * Leave part of yourself burning at one of your own stones (law 31).
   *
   * Costs nothing now and something every day after, which is the whole
   * decision: an aura is a standing claim on a god's regeneration, and three
   * of them consume one entirely. It reaches the world as a *condition*, not
   * an event — the ground simply becomes good over a season and nobody ever
   * sees a god do anything.
   */
  kindleAura(
    anchorId: string, verb: AuraVerb, now: number,
    deityId: string = this.pantheon.player.id,
  ): { ok: boolean; reason?: string } {
    const a = this.anchors.all().find((x) => x.id === anchorId);
    if (!a) return { ok: false, reason: 'no-such-anchor' };
    const r = this.auras.kindle(a, verb, deityId, now);
    return r.ok ? { ok: true } : { ok: false, reason: r.why };
  }

  /**
   * Take hold of the ground and move it (law 20 — Grasp, at the scale where
   * the object *is* the land). Earth is conserved: what comes out of the pit
   * is exactly what goes into the mound, and the mound's height is solved
   * from the pit's volume, so dropping a wide scoop into a narrow spot builds
   * something taller than the hole was deep.
   *
   * Priced by volume against `EARTH_PER_POWER`, measured in `tools/earth.ts`:
   * a 30 m × 10 m hill is about a quarter of a god, and a 60 m × 20 m ridge
   * is more than a god has — the impossible end falls out of the curve, as it
   * does for `graspCost`, rather than being a rule.
   *
   * ## The refusals, and why they arrived late
   *
   * This validated the deity and the price and nothing else, for as long as it
   * had no call sites outside `tools/` and `test/`. A verb nobody can invoke
   * cannot be handed a bad argument; a **key** can, and every one of these was
   * reachable the moment one was bound (`tools/handful.ts` prices them):
   *
   *  · **Not finite.** `cost > god.power` is FALSE for NaN, so a NaN radius
   *    passed the affordability gate, set `god.power` to NaN for the rest of
   *    the world's life and NaN'd every height in the touched chunk. Measured.
   *  · **Not land.** `shapeLand` has always refused on water; this did not, so
   *    a scoop in the sea raised ten metres of seafloor nobody can see.
   *  · **Too far.** `grasp` refuses past `GRASP_REACH` of the Presence and
   *    this had no locality at all — a god could dig the far side of the
   *    island from where it stood, which is law 23's *never an editor*. The
   *    `presence` argument is optional, exactly as it is for `act`: absent
   *    means an unlocated act, which is what a probe and a rival god make.
   *    **Both ends are bounded**, as they are for `grasp`: the first version
   *    of this bounded only the pit, and a scoop taken at your feet could be
   *    thrown eleven kilometres.
   *  · **Back into its own hole.** A pile dropped inside the pit it came from
   *    cancels it exactly, and the god has paid full price for flat ground.
   *    `minThrow` is where `lensFall` reaches zero.
   *  · **A spire.** The price reads `from` alone, so the same 0.27 that buys a
   *    30 × 10 m hill bought a 90 m needle if the drop radius was small
   *    enough. The drop may still concentrate — that is the point of moving
   *    earth — but not without limit.
   */
  moveEarth(
    from: { x: number; z: number; r: number; depth: number },
    to: { x: number; z: number; r: number },
    now: number,
    deityId: string = this.pantheon.player.id,
    /**
     * Where the god is standing. `y` is ABSOLUTE world height, not clearance
     * — the reach test subtracts the ground itself — and passing `alt` here
     * would read every act as happening at sea level. Written down because
     * `tools/level.ts` once shipped a `moveEarth` call missing an argument
     * that `tsx` transpiled into a silent NaN.
     */
    presence?: { x: number; y?: number; z: number },
    /**
     * How far the god may work from where it stands, for the pit AND the
     * pile. Defaults to the arm its own reach permits — `focusFor` of its
     * altitude ceiling, which is the same curve that decides how wide a cast
     * it may choose, so a god's arm grows with its reach and with nothing
     * else. The caller passes the *current* focus, which is never wider.
     */
    arm?: number,
  ): { ok: boolean; reason?: string; cost?: number; height?: number; eventId?: string } {
    const god = this.pantheon.get(deityId);
    if (!god) return { ok: false, reason: 'unknown-god' };

    // Everything downstream is arithmetic on these six numbers, and one NaN
    // among them is permanent: it reaches the god's power and the store both.
    for (const v of [from.x, from.z, from.r, from.depth, to.x, to.z, to.r]) {
      if (!Number.isFinite(v)) return { ok: false, reason: 'not-a-place' };
    }
    if (from.r <= 0 || to.r <= 0 || from.depth <= 0) {
      return { ok: false, reason: 'nothing-to-move' };
    }
    // Dry ground, tested by HEIGHT and not by `siteAt().land`.
    //
    // `shapeLand` uses the land mask and is right to: it accelerates flow and
    // slope, which are quantities the 260 m region lattice carries, so the
    // mask is the same resolution as the thing being hurried. This moves
    // height, and height is bicubic — the mask is a nearest-cell read, which
    // `tools/shore.ts` measured calling **13.95 km² of real headland ocean**.
    // A god refused on a beach it is standing on would be the mask's error
    // arriving as a rule, so the test is the one the water plane itself uses.
    if (this.region.heightAt(from.x, from.z) <= 0 ||
        this.region.heightAt(to.x, to.z) <= 0) {
      return { ok: false, reason: 'not-land' };
    }
    // How far a god may work from where it stands. Three dimensions, unlike
    // `grasp`'s horizontal test, which reads a god hovering a kilometre
    // straight up as standing on the thing — and measured against the ground
    // at each end, not against sea level, so a dig high on a mountain is as
    // local as one on the shore.
    //
    // The arm is the god's own focus and not `GRASP_REACH`. That was the
    // first rule here and it made the faculty unreachable: at 60 m the god is
    // standing inside its own mound, and at the 200–400 m the game is played
    // and *verified* at, nothing at any pitch is in reach. Attention is
    // already the scale of an act and is already capped by earned reach, so
    // "you may take hold of any ground your attention covers, and send it
    // anywhere else your attention covers" costs no new constant and keeps
    // law 23's locality as a bound the player can see themselves widening.
    // Never shorter than the object grasp's own arm.
    const span = Math.max(GRASP_REACH,
      arm ?? focusFor(god.maxAltitude).radius);
    if (presence) {
      const far = (x: number, z: number) => {
        const up = presence.y === undefined ? 0 : presence.y - this.region.heightAt(x, z);
        return Math.hypot(presence.x - x, up, presence.z - z) > span;
      };
      if (far(from.x, from.z) || far(to.x, to.z)) {
        return { ok: false, reason: 'too-far' };
      }
    }
    if (Math.hypot(to.x - from.x, to.z - from.z) < minThrow(from.r)) {
      return { ok: false, reason: 'too-near' };
    }
    // A pit that breaks the sea plane is a hole full of water the renderer
    // draws no water in — `render/water.ts` bakes its depth and foam grading
    // once, from the baseline, so a coastal scoop sits under a translucent
    // sheet that still says "0 m deep". 1.3% of the island's land is under
    // ten metres, and it is exactly the part people build on.
    if (this.region.heightAt(from.x, from.z) - from.depth <= 0) {
      return { ok: false, reason: 'the-sea-would-come-in' };
    }

    const volume = Math.abs(lensVolume(from.r, from.depth));
    // Earth that will not lie where it is put. The price reads `from` alone,
    // so concentration is free — and a 30 × 10 m scoop tipped into a ten-metre
    // circle stands ninety metres tall, which is not a hill, it is a needle.
    // The refusal is the same angle the scoop itself is dug at, so the rule is
    // one sentence: a god may move earth, and earth lies at its repose.
    // The binding clamps to this before it ever asks, so the player meets it
    // as a drop ring that will not tighten further rather than as an error.
    if (ampForVolume(to.r, volume) > to.r * REPOSE) {
      return { ok: false, reason: 'will-not-stand' };
    }
    const cost = earthCost(volume);
    if (cost > god.power) return { ok: false, reason: 'too-weak', cost };

    god.power = clamp(god.power - cost, 0, 1);
    const { piled, dirty } = this.earth.move(from, to);
    for (const k of dirty) this.dirty.add(k);

    // The land itself is the witness. One event, at the mound, so anyone near
    // feels *something happened here* — and, as with every divine act, has no
    // idea what or whose (law 10).
    const ev = this.events.emit({
      type: 'divine_earth', time: now, x: to.x, z: to.z,
      radius: Math.max(to.r, from.r) * 2, magnitude: 1, salience: 0.8,
      cause: { kind: 'divine', godId: deityId },
      // How much was moved, so the Chronicle can decide whether the world
      // remembers it (`EARTH_MARK_VOLUME`). A scoop's size is the player's
      // choice rather than the world's, so nothing upstream of the record can
      // filter it — unlike a fire, whose size the world decides and which is
      // therefore filtered at the emission instead.
      data: { volume },
    });
    return { ok: true, cost, height: piled.amp, eventId: ev.id };
  }

  /** What the land here is already doing, and how hard. Reads nothing, writes nothing. */
  geologyAt(x: number, z: number): GeoReading {
    return readGeology(this.region, x, z, this.earth);
  }

  /**
   * Strengthen or Weaken the geological process already running here (law 28
   * — *godhood can accelerate causality, it cannot escape it*, applied to
   * rock and water).
   *
   * The counterpart to `moveEarth`, and the contrast is the design: Grasp
   * puts a hill wherever you like and charges full price for every cubic
   * metre; this only makes the land do **more of what it was already doing**,
   * and on ground that is doing it hard it is about **20× the earth per unit
   * of god** — three times cheaper and six times deeper (`tools/erosion.ts`).
   * A god that understands its valley moves more than a god that is strong.
   *
   * Refuses on ground where nothing is happening, which is 42% of the island:
   * you cannot accelerate a process that is not running.
   */
  shapeLand(
    verb: 'strengthen' | 'weaken',
    x: number, z: number,
    radius: number,
    now: number,
    deityId: string = this.pantheon.player.id,
    strength = 1,
  ): {
    ok: boolean; reason?: string; process?: string; rate?: number;
    volume?: number; cost?: number; moved?: number;
  } {
    const god = this.pantheon.get(deityId);
    if (!god) return { ok: false, reason: 'unknown-god' };
    if (!this.region.siteAt(x, z).land) return { ok: false, reason: 'not-land' };

    const plan = shapeLand(this.region, this.earth, x, z, radius, verb, strength);
    if (!plan) return { ok: false, reason: 'nothing-happening' };

    const cost = earthCost(plan.volume) * plan.costFactor;
    if (cost > god.power) {
      return { ok: false, reason: 'too-weak', cost, process: plan.reading.process,
               rate: plan.reading.rate };
    }

    god.power = clamp(god.power - cost, 0, 1);
    const { dirty, moved } = applyShape(this.earth, plan, x, z);
    for (const k of dirty) this.dirty.add(k);

    // One event, at the acted point. Unexplained like every divine act — a
    // mortal watching a hillside give way learns that it gave way, and
    // nothing about a hand (law 10). The land was going to do this anyway;
    // it simply did it today.
    this.events.emit({
      type: 'divine_earth', time: now, x, z,
      radius: plan.to.r * 2, magnitude: plan.reading.rate, salience: 0.7,
      cause: { kind: 'divine', godId: deityId },
      data: { process: plan.reading.process },
    });

    return { ok: true, process: plan.reading.process, rate: plan.reading.rate,
             volume: plan.volume, cost, moved };
  }

  /** Put one out. Free, and silent — stopping is not an act. */
  douseAura(anchorId: string): { ok: boolean; reason?: string } {
    const a = this.anchors.all().find((x) => x.id === anchorId);
    if (!a) return { ok: false, reason: 'no-such-anchor' };
    const r = this.auras.douse(a);
    return r.ok ? { ok: true } : { ok: false, reason: r.why };
  }

  /**
   * The single divine entrypoint: one verb, one place, one moment, at one
   * scale of attention. The event is emitted first — the sky flashes whether
   * or not anything catches — then the physical change lands through the same
   * fields everything else reads. Dirty chunk keys accumulate internally;
   * drainDirtyChunks() reports them.
   *
   * The focus is the player's zoom, resolved by focusFor() outside the
   * simulation and passed in as two plain numbers. Its radius becomes the
   * event's radius, which is what band witnessing reads — so widening the
   * cast automatically widens who could have seen it. Its concentration
   * scales the stamp: the same verb is pruning at Presence and a curse at
   * Valley. Omit it and the act is the historical 420 m disc.
   *
   * `ok` is whether the verb found purchase: for strike, whether any bolt
   * ignited; for the others, whether the aim is land at all. The divine_*
   * event emits regardless — a miracle nobody needed is still a miracle.
   */
  /**
   * Pick something up and put it down somewhere else — Design IV §21–§27.
   *
   * Not an Expression and not a cast: Grasp is a faculty of Presence, so it
   * takes no Focus and gets no environmental leverage. It takes hold of one
   * real object and the world carries on from wherever it lands. *Physical
   * manipulation creates causes; the simulation creates consequences* — a log
   * dropped across a creek is a crossing, and nobody had to write a bridge.
   *
   * Bounded exactly as IV §25 asks: near the Presence only, priced steeply by
   * mass and attachment, and refused rather than overdrawn when the god has
   * nothing left. The impossible end of the scale is not special-cased — a
   * thing heavy enough simply costs more than a whole god.
   *
   * Today it reaches the loose sub-metre world (`world/litter.ts`), which is
   * where Grasp belongs first: those things are already lying on the ground,
   * already pointable in O(1), and already identified by place.
   */
  /**
   * WHAT THE HAND WOULD CLOSE ON AT THIS POINT — resolved from the world, not
   * taken from the caller.
   *
   * `grasp` used to be `litterAt(fromX, fromZ, …)` and nothing else, which
   * made the simulation's Grasp **litter-only by construction, one layer below
   * anything the UI could decide**. `main.ts` was the visible narrow part and
   * this was the real one: `isThing` admitted ten kinds, the routing offered
   * them, and the sim could execute exactly one.
   *
   * It stays a resolution BY COORDINATE rather than by an identity handed in,
   * and that is deliberate. `act` takes a target hint and never checks it —
   * "the simulation does not resolve targets, own them, or check them" — but a
   * cast lands on ground either way, and a grasp *moves a specific thing*. A
   * caller that could name the subject could name one that is not there, or
   * one across the island. Reading the lattice back is what makes the world
   * the authority on what the hand found.
   *
   * **Relics outrank litter**, which is the pointer's own precedence
   * (`main.ts` offers a relic before a pebble and says why: under a metre-scale
   * tolerance *some* pebble is always nearer, so a once-in-a-valley find would
   * lose every tie to a twig). Two layers, one order, in both places.
   *
   * Nothing here decides whether the thing can actually be moved. `graspCost`
   * does, out of mass and attachment — a spring prices at thirty whole gods
   * because `relicAttachment` calls it 1 and `relicMass` weighs the hillside,
   * which is the design saying *it is not a thing, it is a place* in the only
   * units the world has. There is no kind table anywhere on this path (law 13).
   */
  private graspableAt(x: number, z: number, now: number): {
    key: string; kind: string; size: number; x: number; z: number;
    mass: number; attach: number; living: boolean; already: boolean;
  } | null {
    /**
     * FIRST, ANYTHING A GOD HAS ALREADY PUT DOWN HERE.
     *
     * Without this a thing could be moved exactly once and then never again:
     * the lattice answers where the world GREW something, and a stone that has
     * been carried is standing somewhere the lattice knows nothing about. A
     * hand that can move a stone one time and not a second is not a hand, it
     * is a one-shot effect — and `CarriedStore` was already built for the
     * repeat, keying on origin so a thing moved twice is one entry rather than
     * two and counting `lifts` as it goes.
     *
     * Its own footprint is the reach, exactly as it is for the lattice below.
     */
    for (const c of this.carried.near(x, z, 0.5)) {
      if (Math.hypot(c.x - x, c.z - z) > Math.max(1, c.size)) continue;
      const relKind = (RELIC_KINDS as readonly string[]).includes(c.kind);
      return {
        key: c.key, kind: c.kind, size: c.size, x: c.x, z: c.z,
        mass: relKind ? relicMass(c.kind as RelicKind, c.size)
                      : litterMass(c.kind as LitterKind, c.size),
        attach: relKind ? relicAttachment(c.kind as RelicKind)
                        : litterAttachment(c.kind as LitterKind),
        living: relKind ? false
          : (LIVING[KIND_NAME.indexOf(c.kind as LitterKind)] ?? false),
        already: true,
      };
    }
    // A metre of slack, not a search: the pointer hands back the thing's own
    // centre, and this only has to survive the float round trip.
    const rel = relicNear(x, z, 1, (qx, qz) => this.region.siteAt(qx, qz));
    if (rel) {
      return {
        key: rel.key, kind: rel.kind, size: rel.size, x: rel.x, z: rel.z,
        mass: relicMass(rel.kind, rel.size), attach: relicAttachment(rel.kind),
        // Not one of the seven is alive. A lightning tree is a dead snag and a
        // bone field is bone; `graspDamage`'s living flag is about roots that
        // mind being torn, and nothing here has any.
        living: false, already: false,
      };
    }
    // The wear sampler is the same one the pointer and the renderer pass, and
    // it is here for the same reason: over a beaten camp floor the world keeps
    // 44% of its litter, and without this a god could Grasp a clump of sedge
    // that nothing is drawing and nothing is standing there. Wear is sparse
    // HISTORY, not a render budget — the budget case is deliberate and
    // documented, this one is a disagreement.
    //
    // Law 7 holds: `world/wear.ts` imports only `@/core/rng`, `./chunk` and
    // `./overlay`, and `chunk.ts` in turn imports only `core/rng`, `./types`
    // and a TYPE-only `./region`. Nothing on that path touches three, so
    // `test/sim-purity.ts` stays green — checked, not assumed.
    const lit = litterAt(x, z,
      (qx, qz) => this.region.siteAt(qx, qz), (qx, qz) => this.region.heightAt(qx, qz),
      (qx, qz) => wornAt({ at: (wx, wz, t) => this.bands.pressureAt(wx, wz, t) },
                         qx, qz, now));
    if (!lit) return null;
    return {
      key: lit.key, kind: lit.kind, size: lit.size, x: lit.x, z: lit.z,
      mass: litterMass(lit.kind, lit.size), attach: litterAttachment(lit.kind),
      // `graspDamage`'s boolean means "this thing has roots and tears rather
      // than lifting". `LIVING` is the general form and the two cannot drift,
      // because it is the same table `wornKeep` reads to decide what a
      // trampled camp floor keeps.
      living: LIVING[KIND_NAME.indexOf(lit.kind)] ?? false,
      already: false,
    };
  }

  /**
   * WHAT THE HAND WOULD FIND HERE, and what it would take of this god.
   *
   * The bar has never been able to price Grasp. `verbCostBands` runs every
   * verb through `costOf`, and Grasp is not priced by radius and distance at
   * all — it is `graspCost(mass, attachment)`, a different function of
   * different arguments — so the palette printed a literal dash under the one
   * verb whose cost varies by three orders of magnitude between a twig and a
   * standing stone. A dash is the worst possible answer there: the price IS
   * the information.
   *
   * It goes through `graspableAt`, so the quote and the bill resolve the same
   * subject by the same rule. Pure: a read of the lattice and the carried
   * store and some arithmetic (law 9).
   */
  graspPrice(x: number, z: number, now: number,
             deityId: string = this.pantheon.player.id):
    { kind: string; mass: number; cost: number } | null {
    const item = this.graspableAt(x, z, now);
    if (!item) return null;
    if (!item.already && this.carried.at(item.key)) return null;
    return {
      kind: item.kind,
      mass: item.mass,
      cost: graspCost(item.mass, item.attach) * this.mod(deityId, 'grasp.cost.attachment'),
    };
  }

  grasp(
    fromX: number, fromZ: number, toX: number, toZ: number, now: number,
    deityId: string = this.pantheon.player.id,
    /** Where the god is. Grasp is local (IV §29) and this is what enforces it. */
    presence?: { x: number; z: number },
  ): GraspResult {
    // The wear sampler is the same one the pointer and the renderer pass, and
    // it is here for the same reason: over a beaten camp floor the world keeps
    // 44% of its litter, and without this a god could Grasp a clump of sedge
    // that nothing is drawing and nothing is standing there. Wear is sparse
    // HISTORY, not a render budget — the budget case is deliberate and
    // documented, this one is a disagreement.
    //
    // Law 7 holds: `world/wear.ts` imports only `@/core/rng`, `./chunk` and
    // `./overlay`, and `chunk.ts` in turn imports only `core/rng`, `./types`
    // and a TYPE-only `./region`. Nothing on that path touches three, so
    // `test/sim-purity.ts` stays green — checked, not assumed.
    const item = this.graspableAt(fromX, fromZ, now);
    if (!item) return { ok: false, reason: 'nothing-there' };
    // The lattice grows a thing at its cell for ever; the carried store is the
    // record that it is not lying there any more. Only asked of a subject the
    // LATTICE produced — one already carried was resolved from that store and
    // is standing exactly where this says it is.
    if (!item.already && this.carried.at(item.key)) {
      return { ok: false, reason: 'nothing-there' };
    }

    const near = presence ?? { x: fromX, z: fromZ };
    if (Math.hypot(near.x - item.x, near.z - item.z) > GRASP_REACH ||
        Math.hypot(near.x - toX, near.z - toZ) > GRASP_REACH) {
      return { ok: false, reason: 'too-far' };
    }

    const { mass, attach } = item;
    /**
     * …and the Godhood, which had machinery here and no wire.
     *
     * `grasp.cost.attachment` is what the Epic *The Divine Hand* sells —
     * "roots and foundations barely slow you" — against `graspCost`'s
     * `1 + 3.2 × attachment` hold term, and `grasp.damage` is its other half,
     * "and what you lift survives being lifted". Both were marked `built` in
     * `bindings.ts` and read by nothing, which is the state `WIRING.md` A1b
     * describes for roughly forty variables: the simulation could already
     * answer and was never asked.
     *
     * `deityId` is already in scope — the god is fetched on the next line —
     * so this is the whole cost of wiring them.
     */
    const cost = graspCost(mass, attach) * this.mod(deityId, 'grasp.cost.attachment');
    /**
     * A GOD THAT IS NOT IN THIS WORLD SPENDS NOBODY'S POWER.
     *
     * This read `?? this.pantheon.player`, which is the single most dangerous
     * line in the multiplayer seam: a wrong, stale or unknown `deityId` fell
     * back to the local player and **charged them for a cast they did not
     * make**, silently, with the event still filed under the id that was
     * passed. In one world that is a typo; in the shared world `DECISIONS.md`
     * describes it is one client billing another.
     *
     * Refusing is the only safe reading. A god that has left the world, or was
     * never in it, cannot act in it — and the caller finds out rather than the
     * player paying for it.
     */
    const god = this.pantheon.get(deityId);
    if (!god) return { ok: false, reason: 'no-such-god' };
    if (god.power < cost) return { ok: false, reason: 'too-weak', cost, mass };
    god.power = clamp(god.power - cost, 0, 1);

    // `graspDamage`'s boolean means "this thing has roots and tears rather than
    // lifting" — it was written when sedge was the only living kind on the
    // lattice, and there are six now. Ferns, moss, flowers, mushrooms and seed
    // heads were all being torn up for free. `LIVING` in `world/litter.ts` is
    // the general form and the two cannot drift, because it is the same table
    // `wornKeep` reads to decide what a trampled camp floor keeps.
    const damage = clamp(
      graspDamage(attach, item.living) * this.mod(deityId, 'grasp.damage'), 0, 1);
    for (const k of this.carried.place(item.key, item.kind, item.size, item.x, item.z,
                                       toX, toZ, now, damage)) this.dirty.add(k);

    // A witnessable physical event, and — as ever — one that names what
    // happened and never whose hand it was. A mortal who sees a stone rise
    // and travel has something enormous to explain and nothing to explain it
    // with, which is where religion comes from (law 10).
    const ev = this.events.emit({
      type: 'divine_grasp', time: now, x: toX, z: toZ,
      radius: 40, magnitude: clamp(mass / 200, 0.05, 1), salience: 0.75,
      cause: { kind: 'divine', godId: deityId },
      data: { thing: item.kind },
    });
    return { ok: true, eventId: ev.id, cost, mass, damage, key: item.key };
  }

  act(
    verb: 'strike' | 'rain' | 'strengthen' | 'weaken' | 'omen',
    x: number,
    z: number,
    now: number,
    focus: DivineFocus = FOCUS_REFERENCE,
    /**
     * Whose hands. The event carries it as its true cause — the world's own
     * record of who did what — and nothing that can be witnessed carries it
     * onward: to every eye in the world a divine act is simply unexplained.
     * A rival god acts through this exact call, which is why item 10 needs
     * no new machinery and no hidden world-editing.
     */
    deityId: string = this.pantheon.player.id,
    /**
     * What the act was aimed *at*, when it was aimed at a thing rather than at
     * ground. The world writes it into the event, so history records that
     * something happened to *that tree* and not merely near it — and because
     * it rides in `data`, it survives `perceive()` and reaches witnesses.
     *
     * That is deliberate and it is the point: **what** happened is legible,
     * **who** did it never is. A mortal standing under a tree that burst into
     * leaf knows exactly which tree, and no more about the hand than they ever
     * did. Which is the seam religion grows out of — a particular thing,
     * unexplained.
     *
     * The simulation does not resolve targets, own them, or check them. It
     * receives two strings from whoever was pointing, exactly as it receives
     * two numbers for the focus.
     */
    target?: {
      kind: string; id: string;
      /**
       * A herd's or an animal's census cell and kind, when the target is one.
       * The simulation cannot recover it from the id — a herd id carries a
       * render seed, not a cell — and must not recover it from (x, z), because
       * a drawn herd's anchor may lean out of its cell by design. So the
       * pointer, which dealt the herd from that cell, says which. Two ints and
       * a string, exactly as the focus is two numbers.
       */
      herd?: { species: string; sx: number; sz: number };
      /**
       * The plant's own kind, when the target is one — the pointer says
       * which, exactly as it says a herd's cell, because the simulation
       * cannot recover a drawn plant's species from its id and must not
       * re-derive the world's vegetation to ask. What it buys (2026-08-19,
       * the wake): a channelled Strengthen or Weaken on a HOSTED plant kind
       * reads the plant's own strain out of the ground's mixture — the same
       * `drawStrain` the renderer uses, so the god presses what the eye sees
       * — feeds its tolerance into the cast's viability (a hardy variety is
       * viable further into the dry), and PRESSES the strain field toward
       * (Strengthen) or away from (Weaken) that variety. An ordinary plant
       * takes the ordinary cast and presses nothing: there is no variety in
       * it to reinforce, which is `ROADMAP §45.4`'s refusal kept honest.
       */
      plant?: { kind: string };
      /**
       * Whether the cast is CHANNELLED into this thing, or merely centred on
       * it. Defaults to true, which is every caller that existed before
       * semantic scale did.
       *
       * The distinction is the whole of `PRECISION × SCALE × INTENSITY`. A
       * channelled cast collapses onto the thing's own footprint and the
       * named subject bears the full press wherever they stand in the disc —
       * naming them is what the aim was for. A cast the player has *scrolled
       * out* from is still about them (they are why the god is here, and they
       * are still the card's subject) but they are no longer privileged: the
       * pressure lands on the working group, and the world decides which
       * individuals carry it. More scale, less authorship.
       *
       * `aimed: false` therefore keeps the press and drops both the full
       * weight and the event's target record — because at a valley's width
       * *this happened to that person* is no longer a true sentence.
       */
      aimed?: boolean;
    },
    /**
     * Where the god was standing when it acted. Design III §88: acting where
     * you are is the cheapest thing a god can do, and acting across the island
     * is not — which is what makes travel matter. Omitted means *at the cast*,
     * the cheapest rate, so every existing caller keeps the behaviour it had.
     */
    from?: { x: number; z: number },
    /**
     * HOW MUCH OF ITSELF THE GOD PUTS INTO THIS (2026-08-18, Griffin's
     * direction): a share of the god, in stones — `IMPULSE_STONE` for a tap of
     * the key, up to everything the god has left for a key held down. What is
     * given is spent, whole; a cast under one stone is refused; and what the
     * cast DOES scales with the stones over the first (a tap is the cast as
     * it always was) — through the same leverage `costOf` prices, so working
     * with the land, near, and tight still buys more per stone than working
     * against it, far and wide (law 17 kept as efficiency rather than price).
     * Omitted means the old economy — the quote itself is the bill — which is
     * every rival's act, every probe and every test.
     */
    spend?: number,
  ): DivineResult {
    const spec = DIVINE_VERBS[verb];
    const on = target && target.aimed !== false
      ? { data: { targetKind: target.kind, targetId: target.id } } : {};
    const radius = Math.max(1, focus.radius);
    const concentration = Math.max(0, focus.concentration);
    /**
     * `event.salience.divine` — how loudly this god's acts land in the world.
     *
     * Salience is what decides whether anybody REMEMBERS: `worthRemembering`
     * drops anything under `SALIENT`, and `memorability` weights what survives
     * eviction. So a god that has invested here is not louder in any visible
     * way — it is more *memorable*, which is the one currency religion is
     * actually made of.
     *
     * Law 10 is untouched and is worth saying twice: a witness receives a
     * salience, never a hand. Two gods casting the same verb differ in how
     * much the world keeps of it and in nothing else that can be pointed at.
     */
    const salience = clamp(
      spec.salience * salienceScale(radius) * this.mod(deityId, 'event.salience.divine'),
      0, 1);
    /**
     * WHAT THE CAST DOES, as against how wide and how tight it is — and the
     * two have to be different numbers.
     *
     * `concentration` is the geometry of the gesture. It is returned to the
     * caller, and the reticle and the FX draw the cast off it, so a Godhood
     * multiplier applied there would make a god's spectacle grow with its
     * build rather than with the cast it actually chose. `work` is the same
     * number after the Web has spoken, and it is used only where effect is
     * *delivered*.
     *
     * The split at `FOCUS_REFERENCE.radius` is not arbitrary and is not a
     * tuning constant: that radius is where `focusFactor === 1`, the fixed
     * point every verb in the game was priced against. Below it a cast is
     * tight and *One Perfect Act* answers for it; above it the cast is wide
     * and *The Gardener* does. Law 26's asymmetry, with the dividing line at
     * the one place the system already calls neutral.
     *
     * Deliberately NOT applied inside `concentrationFor`: that function is
     * deityless and is also what `focusForRadius`/`focusFor` hand the HUD, so
     * modifying it there would change what the player is shown for a cast they
     * have not paid for.
     */
    let work = concentration * this.mod(deityId,
      radius <= FOCUS_REFERENCE.radius ? 'act.work.tightFocus' : 'act.work.wideFocus');

    // ---- what it costs, and whether there is enough god left to pay ----
    // Design III §86–§90. Before this, a cast was free and a whisper over one
    // tree cost exactly what a storm across a watershed did — so §87's
    // "precision and scale become strategic choices" had nothing to choose
    // between, and §179's silence could not be strategically valid because
    // there was nothing to preserve by keeping it.
    // …and the same refusal here: see `grasp`. A cast by a god this world does
    // not hold spends nothing and changes nothing.
    const god = this.pantheon.get(deityId);
    if (!god) return { ok: false, radius, concentration, reason: 'no-such-god' };
    const site = this.region.siteAt(x, z);
    const env = this.env.at(x, z, now);
    let anchorNear = Infinity;
    for (const a of this.anchors.all()) {
      if (a.godId !== deityId) continue;
      anchorNear = Math.min(anchorNear, Math.hypot(a.x - x, a.z - z));
    }
    const cost = costOf(verb, radius, from ? Math.hypot(from.x - x, from.z - z) : 0,
      { rain: site.rain, biomass: env?.biomass ?? 0, moisture: env?.moisture ?? 0 },
      anchorNear,
      // The Godhood, at last. `act.cost` is the whole bill and
      // `impulse.cost.distance` is the reach term alone, so a god that has
      // invested in Reach pays less for working far away WITHOUT everything
      // becoming cheap — which is law 26's asymmetry expressed as two numbers
      // instead of one. `costOf` is the same function the HUD's preview calls,
      // so the quote and the bill cannot drift apart.
      { all: this.mod(deityId, 'act.cost'),
        reach: this.mod(deityId, 'impulse.cost.distance'),
        focus: this.mod(deityId, 'act.cost.tightFocus'),
        // …and what the cast is aimed at: Beasts' *Light Hand* prices acting
        // on animals. `main.ts`'s `castModsFor` is the quote's twin of this.
        target: target?.kind === 'herd' || target?.kind === 'animal'
          ? this.mod(deityId, 'impulse.cost.beastTarget') : 1 });
    // §131: power should never remove choice, it should make choices more
    // consequential — so a god too spent to act is told so and nothing
    // happens, exactly as the Anchor already refuses.
    let paid = cost.total;
    /**
     * A tap is one stone; a hold is more, and more stones are more work.
     * `intensity` is stones over the first times the leverage the quote
     * measured — `base / total` is 1 at the reference (a with-the-land cast
     * of reference width, at hand, for a god with no discounts), under it
     * against the land, far or wide, and a little over it for a god whose
     * Godhood bought casting down. Clamped so a stone is never worth nothing
     * and never worth a whole charge.
     */
    let intensity = 1;
    if (spend !== undefined) {
      const stones = clamp(spend, 0, god.power);
      if (stones + 1e-9 < IMPULSE_STONE) {
        return { ok: false, radius, concentration, cost: IMPULSE_STONE, reason: 'too-weak' };
      }
      // Might's *Unbreakable Purpose*, wired the day a held cast existed to
      // wire it to: "holding something is cheap" is the price of the stones
      // past the first, "starting it is weak" is a one-stone cast's work
      // (`act.cost.sustained`, `act.work.burst` — `bindings.ts`). The stones
      // still count in full toward what the cast does and toward the arc.
      const held = Math.max(0, stones - IMPULSE_STONE);
      paid = IMPULSE_STONE + held * this.mod(deityId, 'act.cost.sustained');
      const leverage = clamp(cost.base / Math.max(1e-9, cost.total), 0.35, 1.5);
      intensity = (stones / IMPULSE_STONE) * leverage;
      if (held < 1e-9) intensity *= this.mod(deityId, 'act.work.burst');
    } else if (god.power < cost.total) {
      return { ok: false, radius, concentration, cost: cost.total, reason: 'too-weak' };
    }
    god.power = clamp(god.power - paid, 0, 1);
    // …and the arc: every stone put into the world counts toward the level,
    // a charged cast for all of its stones (`GodState.spend`; `god.ts`)
    god.spend((spend !== undefined ? clamp(spend, 0, 1) : paid) / IMPULSE_STONE);
    // Might's *By Divine Will* and *The Great Act*: "everything you do lands
    // harder" — the named Divine Work term `bindings.ts` said did not exist
    work *= intensity * this.mod(deityId, 'act.work');

    // §4, persistence follows significance. A verb channelled into a *thing*
    // does not only happen near it: it accumulates against that thing's own
    // standing, and enough of it makes an ordinary tree an important one for
    // the next several human lifetimes. Three things about this are load-
    // bearing:
    //
    //  · Only things whose identity is their place. People, structures,
    //    trails and anchors carry ids and persist already; bare ground is
    //    what the picker returns for "nothing in particular", and a god who
    //    wants bare ground to matter comes down and anchors it.
    //  · Only what was *meant*, weighted by the cast's concentration against
    //    the tightest cast there is. Measured (`tools/promotion.ts`): every
    //    cast today's interface can channel arrives at weight 1, because
    //    `channelRadius` collapses to 5–40 m and concentration saturates at
    //    its 3.4 ceiling from 138 m down — so the live rule is simply
    //    **three casts on one tree**. The weight is not therefore idle:
    //    `act` takes focus and target as independent arguments, so a caller
    //    that names a thing while casting wide — a rival god, a scripted
    //    world-builder act, the server §7 anticipates — earns proportionally
    //    less, and needs 8 casts at the reference and 42 at a watershed.
    //  · Not Omen. Omen writes nothing into the land, and a status the land
    //    carries for a century is emphatically something in the land. The
    //    informational verb stays informational.
    const promotes: ThingKind | null =
      target?.kind === 'plant' ? 'plant' : target?.kind === 'rock' ? 'rock' : null;
    if (promotes && verb !== 'omen') {
      const weight = clamp(work / CONC_MAX, 0, 1);
      const sign = verb === 'rain' || verb === 'strengthen' ? 1 : -1;
      const m = this.things.mark(promotes, x, z, weight, sign, now);
      for (const k of m.dirty) this.dirty.add(k);
      // The threshold speaks. Repetition is the mechanic `world/things.ts`
      // exists for — three casts into one tree and it stops being ordinary —
      // and until now that crossing happened in silence, so the only way to
      // learn the rule was to be told it outside the game.
      //
      // Law 10 is satisfied mechanically and not by convention: the cause is
      // `divine`, `perceivedCause` collapses that to *unexplained* for every
      // eye in the world, and `test/deity.ts` enforces the wall. A mortal who
      // witnesses this knows a particular tree has become strange. It knows
      // nothing whatever about a hand.
      if (m.crossed) {
        this.events.emit({
          type: 'thing_promoted', time: now,
          x, z, radius: Math.max(12, radius * 0.5), magnitude: Math.abs(m.standing),
          // high: a thing in the world has visibly stopped being ordinary,
          // which is exactly the kind of event a religion is built out of
          salience: 0.72,
          cause: { kind: 'divine', godId: deityId },
          data: { kind: promotes, tended: m.standing > 0, ...on.data },
        });
      }
    }

    // A CAST AIMED AT A PERSON REACHES THE PERSON.
    //
    // It did not, for the whole life of the build. `act` touched the pantheon,
    // the region, the env field, anchors, mods, things, dirty chunks, events
    // and fire — and never `bands`. So the one interaction the game is about
    // (find somebody, learn they are curious, lean on that) ended as a stamp
    // on the grass where they happened to be standing, and the player was
    // right to conclude nothing had happened.
    //
    // Strengthen absorbs Imprint (`DECISIONS.md`), so this is a branch of an
    // existing verb keyed on target kind, not a verb of its own: no
    // `divine_imprint`, no new EventType, no sixth button (law 20).
    //
    // Not Omen, and for the same reason promotion is not Omen: the
    // informational verb writes nothing into the world, and a person's
    // temperament is emphatically something in the world. Omen already reaches
    // minds by the only road it is allowed — being *noticed* — and that road is
    // the richest one in the build.
    // It rides the cast's OWN event rather than emitting a second one. The
    // ring is 2,000 records against ~78,900 events a world-year, so a spare
    // event per cast is not free; and two events for one act would also give
    // the Chronicle two things to credit for one hand.
    let pressed: ReturnType<BandSystem['press']> | null = null;
    if (target?.kind === 'person' && verb !== 'omen') {
      const sign = verb === 'rain' || verb === 'strengthen' ? 1 : -1;
      pressed = this.bands.press(x, z, radius, sign,
                                 clamp(work / CONC_MAX, 0, 1),
                                 now, target.aimed === false ? undefined : target.id,
                                 // the one Become moment: a god who has bought
                                 // *Took to It* reaches what a life lately became
                                 this.rule(deityId, 'press.reach.becoming'));
    }

    /**
     * A CAST AIMED AT A HERD REACHES THE HERD — for a god whose Godhood lets it.
     *
     * Everyone's Strengthen on a herd is what it always was: a stamp on the
     * ground under them, which the habitat reads into tomorrow's K. That does
     * not change here. What the Beasts direction adds is a second thing the
     * same cast can MEAN, and only for a god who has bought the meaning
     * (`beast.press.*` — Migration Thread, Green Years, Hunt as One): a lean
     * on what the herd is already doing — inclined to leave, gathering onto
     * room, hunting prey — delivered to `WildlifeSystem.lean`, which decides
     * from the herd's own state which of those it is doing and how much, and
     * refuses honestly when it is doing none of them (law 21, structural).
     *
     * `beast.work.movement` scales the delivered press; `beast.press.persistence`
     * lengthens its half-life at the deposit (the ledger has no god to ask
     * later, so the god's patience is written into the press itself — the
     * same pattern as `env.stamp`'s persistence). Rules are read through
     * `rule()`, the boolean channel `useGodhood` grew for exactly this.
     *
     * The wide half — `beast.press.movement.wide`, Great Movement — is the
     * same lean cast diffusely: every stocked cell in the disc, every land
     * kind on it that is inclined to move, at the wide cast's own thin
     * concentration and only for movement. More ground, less authorship, and
     * a settled herd is passed over — which is `PRECISION × SCALE × INTENSITY`
     * doing what it says.
     */
    let beast: DivineResult['beast'];
    if ((verb === 'strengthen' || verb === 'weaken')) {
      const sign = verb === 'strengthen' ? 1 : -1;
      const want = {
        movement: this.rule(deityId, 'beast.press.movement'),
        increase: this.rule(deityId, 'beast.press.increase'),
        hunt: this.rule(deityId, 'beast.press.hunt'),
      };
      const halfDays = PRESS_HALFLIFE * this.mod(deityId, 'beast.press.persistence');
      const strength = clamp(work / CONC_MAX, 0, 1);
      if (target?.herd && target.aimed !== false && (want.movement || want.increase || want.hunt)) {
        const amount = sign * PRESS_PER_CAST * strength * this.mod(deityId, 'beast.work.movement');
        const r = this.wildlife.lean(target.herd.sx, target.herd.sz,
          target.herd.species as WildlifeSpeciesId, amount, halfDays, want);
        beast = { process: r.process, delivered: Math.abs(r.delivered) / PRESS_PER_CAST, herds: r.process ? 1 : 0 };
      } else if (this.rule(deityId, 'beast.press.movement.wide')
                 && (!target || target.aimed === false)) {
        const amount = sign * PRESS_PER_CAST * strength * this.mod(deityId, 'beast.work.movement.wide');
        let herds = 0, delivered = 0;
        const { sx: cx, sz: cz } = toSocial(x, z);
        const span = Math.ceil(radius / SOCIAL_CELL_SIZE) + 1;
        // walked in a fixed order over the disc, so the result is a function
        // of the world and never of the ledger's iteration
        for (let dz = -span; dz <= span; dz++) for (let dx = -span; dx <= span; dx++) {
          const c = socialCenter(cx + dx, cz + dz);
          if (Math.hypot(c.x - x, c.z - z) > radius) continue;
          for (const s of LAND_KINDS) {
            const r = this.wildlife.lean(cx + dx, cz + dz, s, amount, halfDays,
              { movement: true, increase: false, hunt: false });
            if (r.process) { herds++; delivered += Math.abs(r.delivered) / PRESS_PER_CAST; }
          }
        }
        beast = { process: herds ? 'movement' : null, delivered, herds };
      }
    }

    // Omen is the informational verb. It emits and stops: no moisture, no
    // biomass, no fuel, not one dirty chunk. Nothing downstream of here can
    // tell it apart from a thing that never happened — except the people who
    // saw it, who now have something to explain. That is the entire act.
    if (verb === 'omen') {
      const ev = this.events.emit({
        type: spec.type, time: now, x, z,
        radius, magnitude: 1, salience,
        cause: { kind: 'divine', godId: deityId },
        ...on,
      });
      return { ok: true, eventId: ev.id, radius, concentration, cost: paid };
    }

    if (verb === 'strike') {
      // a charged strike is a wider storm: more ground, more bolts, the same
      // seed-and-place determinism per bolt (√ so five stones is a storm
      // 2.2× across, not 5×)
      const sites = strikeSites(this.region.seed, x, z, radius * Math.sqrt(intensity), now);
      const ev = this.events.emit({
        type: spec.type, time: now, x, z,
        radius, magnitude: sites.length, salience,
        cause: { kind: 'divine', godId: deityId },
        ...on,
      });
      for (const { bandId, crossing } of pressed?.became ?? []) {
        this.bands.became(bandId, crossing, now, [ev.id]);
      }
      // every bolt is resolved in order, so the storm replays identically
      const bolts: DivineBolt[] =
        sites.map((s) => ({ x: s.x, z: s.z, lit: this.fire.ignite(s.x, s.z, now) }));
      for (const b of bolts) {
        if (!b.lit) continue;
        // each fire is its own event, and the simulation knows its father —
        // through parentEventIds, which perceive() strips. The fire itself is
        // a NATURAL event: the bolt was divine, the burning is just burning,
        // and now that the sky lights its own fires (sim/lightning.ts) this
        // is what makes a god's fire deniable. A witness at the fire's edge
        // reads the same natural cause either way; only someone who saw the
        // strike itself has anything to explain. Law 10, both halves: the
        // world records whose hand it was, nothing witnessable carries it.
        const fs = this.events.emit({
          type: 'fire_started', time: now, x: b.x, z: b.z,
          radius: 200, magnitude: 1, salience: 0.8,
          cause: { kind: 'natural' },
          parentEventIds: [ev.id],
        });
        // and the burn remembers what lit it, so the chain reaches all the
        // way: strike → fire_started → fire_burned. Without this the
        // Chronicle credits nothing and `strike|burn+fire` — the tenth kind
        // of consequence — stays unreachable.
        this.fire.creditBurn(fs.id);
      }
      return { ok: bolts.some((b) => b.lit), eventId: ev.id, radius, concentration,
               bolts, cost: paid };
    }

    const ev = this.events.emit({
      type: spec.type, time: now, x, z,
      radius, magnitude: spec.magnitude * work, salience,
      cause: { kind: 'divine', godId: deityId },
      ...on,
      // `touched: 0` is said out loud rather than omitted. A cast on somebody
      // whose day is asking nothing of them moves nothing — law 21's scaling
      // reaching zero honestly — and that is a fact the player needs, not a
      // failure to hide. It is the difference between "the god did nothing"
      // and "there was nothing in them to lean on today".
      ...(pressed ? { data: { ...on.data, touched: pressed.touched } } : {}),
    });
    /**
     * HORIZON C FOR A STRENGTHEN LAID ON A LIFE. A press that carried an axis
     * over a line the card speaks at is a becoming — the person is different
     * in a way the world can say — and the world says so with `person_changed`,
     * descending from this cast through `parentEventIds`. The Chronicle lets
     * the strand endure by that descent (`becoming`), and by nothing else: a
     * press that nudged an axis and crossed nothing endured nothing, however
     * many times it is repeated (law 25). `perceive()` strips the parent, so
     * what any witness could learn is that somebody grew bold. Emitted AFTER
     * the cast, because the child must be able to find its parent.
     */
    for (const { bandId, crossing } of pressed?.became ?? []) {
      this.bands.became(bandId, crossing, now, [ev.id]);
    }
    // The land remembers on a 32 m lattice and can hold nothing finer. A cast
    // tighter than one cell would otherwise either fall between cells and mark
    // nothing, or bleed across the four it touches — a Presence act smeared
    // over four times the ground it aimed at. So it snaps to the cell it
    // landed in and marks exactly that: the concentration, not the footprint,
    // is what makes a small cast intense.
    let sx = x, sz = z, stampRadius = radius;
    if (radius < SCAR_CELL) {
      sx = (Math.floor(x / SCAR_CELL) + 0.5) * SCAR_CELL;
      sz = (Math.floor(z / SCAR_CELL) + 0.5) * SCAR_CELL;
      stampRadius = SCAR_CELL * 0.5;
    }
    // Design IV §14–§15: Strengthen reinforces an *existing viable* process
    // and Weaken diminishes one, so both are scaled by how much of that
    // process is actually happening here. Bare rock has no growth to reinforce
    // and none to take. Moisture is not scaled — introducing water is Rain's
    // job (a Signature, IV §19), not a reinforcement.
    const carrying = BIOMES[site.biome]?.density ?? 0.4;
    /**
     * The target plant's own strain, when the cast is channelled into a
     * hosted plant: the member `drawStrain` deals at the plant's own position
     * — LOD-stable, worker-stable, and exactly what the renderer shows — so
     * the god presses the variety it is looking at. Ordinary resolves to
     * null: nothing to reinforce, nothing to press.
     */
    let plantStrain: { key: string; tolerance: number } | null = null;
    if (target && target.aimed !== false && target.plant
        && (verb === 'strengthen' || verb === 'weaken')
        && (PLANT_HOSTS as readonly string[]).includes(target.plant.kind)) {
      const host = target.plant.kind as StrainHost;
      effectiveMixAt(this.region, this.strains, host, x, z, now, pressMix);
      const member = drawStrain(pressMix, x, z, this.region.seed);
      const key = pressMix.key[member];
      if (!isOrdinary(key)) {
        plantStrain = { key, tolerance: strainOf(this.region.seed, key).traits.tolerance };
      }
    }
    /**
     * …and what this god believes about working with the land.
     *
     * `process.viabilityFloor` is an ADD and can legitimately be negative — an
     * Epic sells −0.15 — so it cannot travel through `mod()`, whose guard
     * rewrites any non-positive to 1. It comes off the bonus channel when
     * there is one; until then it is 0, which is exactly what an unspent Web
     * means. The two multipliers are the ends of the same curve and go through
     * `mod` as normal.
     */
    const viability = processViability(verb, {
      rain: site.rain, biomass: env?.biomass ?? 0, moisture: env?.moisture ?? 0,
      ...(plantStrain ? { tolerance: plantStrain.tolerance } : {}),
    }, carrying, {
      high: this.mod(deityId, 'act.work.highViability'),
      low: this.mod(deityId, 'act.work.lowViability'),
    });
    const stamp = {
      moisture: spec.moisture * work,
      biomass: spec.biomass * work * viability,
    };
    /*
     * How long this god's work lasts — the Root fantasy, and the one thing the
     * Godhood could not sell until the env cells learned to carry it.
     *
     * The Web's variables are DECAY RATES, so a node that halves the rate is a
     * multiplier of 0.5 and the land should hold it twice as long: persistence
     * is the reciprocal. Named separately for moisture and growth because
     * `bindings.ts` names them separately, and because they are different
     * fantasies — `env.effect.decayRate` is a valley that stays wet,
     * `life.effect.decayRate` is a wood that does not forget it was tended.
     */
    // …and a charged cast is not only more, it LASTS: the stones over the
    // first stretch how long the land holds the work (√, so five stones is
    // twice the persistence), because a saturated cell can take no more
    // deviation and a hold that bought nothing would be a hold not worth making
    const linger = Math.sqrt(Math.max(1, intensity));
    const persist = {
      moisture: linger / this.mod(deityId, 'env.effect.decayRate'),
      biomass: linger / this.mod(deityId, 'life.effect.decayRate'),
    };
    for (const k of this.env.stamp(sx, sz, stampRadius, stamp, now, persist)) this.dirty.add(k);

    // ---- and it radiates ------------------------------------------------
    // Griffin's account of what a verb channelled into a THING should do: it
    // hits that thing hardest and then a weaker version of the same effect
    // reaches what is around it — more plants, bushes coming into berry —
    // so significance shows as living things doing something.
    //
    // Without this a channelled cast is the narrowest act in the game: it
    // snaps to ONE 32 m cell (above), which is 0.1 ha, and a god who means a
    // particular tree changes a patch smaller than the tree's own clearing.
    // The apron is what makes "I did that, there" legible at the altitude the
    // game is played at, and it is deliberately a *growth* verb's privilege —
    // Strike is a bolt and Omen writes nothing at all.
    //
    // It is not free power. The apron carries APRON_SHARE of the core's
    // deviation, so the total is roughly a third more than the core alone
    // while covering nine times the ground: a channelled cast trades depth
    // for spread the way every other focus choice in the game does. Cost is
    // already paid on the wheel's radius (`costOf` reads `focus`), so this
    // buys reach with the concentration the player chose, not with nothing.
    if (target && (verb === 'strengthen' || verb === 'weaken' || verb === 'rain')) {
      const apron = Math.min(APRON_MAX, Math.max(stampRadius * 3, APRON_MIN));
      // the apron is the same hand's work and lasts exactly as long: a god
      // whose blessings endure does not get a stubborn tree ringed by ordinary
      // ground that forgets on the usual schedule
      for (const k of this.env.stamp(sx, sz, apron, {
        moisture: stamp.moisture * APRON_SHARE,
        biomass: stamp.biomass * APRON_SHARE,
      }, now, persist)) this.dirty.add(k);
    }
    /**
     * THE VERB ON A VARIETY (2026-08-19, the wake — `ROADMAP §45.1`'s "the
     * verb on a plant is one press call"). Strengthen moves the ground toward
     * the plant's own strain, Weaken away from it; the weight rides the same
     * `work × viability` the growth stamp rides, through `TILT_GAIN`, so a
     * charged with-the-land cast establishes faster — and law 25 holds by the
     * press's own construction: it moves TOWARD a coordinate, so the tenth
     * identical cast is worth `(1−w)⁹` of the first with no tuned curve
     * anywhere. The radius is floored at most of one 260 m cell: a strain is
     * a property of a stand, and a 16 m stamp would fall between the field's
     * cell centres and press nothing.
     */
    if (plantStrain) {
      const w = clamp(TILT_GAIN * work * viability, 0, 1);
      const pressR = Math.max(stampRadius, STRAIN_CELL * 0.75);
      for (const k of this.strains.press(
        x, z, pressR, plantStrain.key, verb === 'strengthen' ? 1 : -1, w, now,
      )) this.dirty.add(k);
    }
    return { ok: this.region.siteAt(x, z).land, eventId: ev.id, radius, concentration,
             cost: paid,
             ...(pressed ? { touched: pressed.touched, named: pressed.named } : {}),
             ...(beast ? { beast } : {}) };
  }

  /**
   * The whole world's sparse history at `worldTime` (spec §49–§51): env,
   * scars, live fire, herds, the mortal layer, and the event log — every
   * counter and rng cursor included, so a restore resumes the same world,
   * not a similar one. Region is never here: it rebuilds from the seed.
   */
  toSnapshot(worldTime: number): SimulationSnapshot {
    return {
      version: SNAPSHOT_VERSION,
      seed: this.region.seed,
      worldTime,
      env: this.env.toSnapshot(),
      overlay: this.overlay.toSnapshot(),
      things: this.things.toSnapshot(),
      sere: this.sere.toSnapshot(),
      strains: this.strains.toSnapshot(),
      carried: this.carried.toSnapshot(),
      earth: this.earth.toSnapshot(),
      chronicle: this.chronicle.toSnapshot(),
      fire: this.fire.toSnapshot(),
      bands: this.bands.toSnapshot(),
      wildlife: this.wildlife.toSnapshot(),
      events: this.events.toSnapshot(),
      pantheon: this.pantheon.toSnapshot(),
      anchors: this.anchors.toSnapshot(),
      cultures: this.cultures.toSnapshot(),
    };
  }

  /**
   * Rebuild a running world from a snapshot. The Region is the caller's —
   * rebuilt from the seed, and checked against the save's, because restoring
   * history onto a different baseline would be quiet nonsense. The returned
   * simulation shares no state with whatever produced the snapshot; advance
   * it from the returned worldTime and it walks the exact future the donor
   * would have walked.
   */
  static fromSnapshot(
    region: Region,
    wind: { x: number; z: number },
    snap: SimulationSnapshot,
  ): { sim: Simulation; worldTime: number } {
    if (snap.version !== SNAPSHOT_VERSION) {
      throw new Error(`snapshot version ${snap.version}, expected ${SNAPSHOT_VERSION}`);
    }
    if (snap.seed !== region.seed) {
      throw new Error(`snapshot is of world ${snap.seed}, not ${region.seed}`);
    }
    const env = snap.env ? EnvField.fromSnapshot(snap.env) : new EnvField();
    const overlay = snap.overlay ? Overlay.fromSnapshot(snap.overlay) : new Overlay();
    const things = snap.things ? ThingStore.fromSnapshot(snap.things) : new ThingStore();
    const sere = snap.sere ? SereStore.fromSnapshot(snap.sere) : new SereStore();
    const strains = snap.strains ? StrainField.fromSnapshot(snap.strains, region) : undefined;
    const sim = new Simulation(region, { x: wind.x, z: wind.z },
                               { env, overlay, things, sere,
                                 ...(strains ? { strains } : {}) });
    // what a god carried off stays carried off. A copy, never a replay —
    // see CarriedStore.restore for why that distinction is load-bearing
    if (snap.carried) sim.carried.restore(snap.carried);
    // the shape of the land a god changed. Optional, so a world saved
    // before anyone could dig restores flat — no version bump owed
    if (snap.earth) sim.earth.restore(snap.earth);
    // the durable record. Optional for the same reason `carried` and `earth`
    // are: a world saved before anything could remember has nothing to
    // remember, so its absence is the truth rather than a lost field — and no
    // `SNAPSHOT_VERSION` bump is owed, which is what keeps a twenty-minute
    // prehistory rebuild off the price of building this
    if (snap.chronicle) sim.chronicle.restore(snap.chronicle);
    if (snap.fire) sim.fire.restore(snap.fire);
    if (snap.wildlife) sim.wildlife.restore(snap.wildlife);
    if (snap.bands) sim.bands.restore(snap.bands);
    if (snap.events) sim.events.restore(snap.events);
    if (snap.pantheon) sim.pantheon.restore(snap.pantheon);
    if (snap.anchors) sim.anchors.restore(snap.anchors);
    if (snap.cultures) sim.cultures.restore(snap.cultures);
    return { sim, worldTime: snap.worldTime };
  }

  /** Chunk keys whose land changed since last drained — feed to invalidate(). */
  drainDirtyChunks(): string[] {
    for (const k of this.bands.drainDirtyChunks()) this.dirty.add(k);
    const out = [...this.dirty];
    this.dirty.clear();
    return out;
  }

  /** 0..1: how much actively burning fire stands near a point. */
  private fireDanger(x: number, z: number): number {
    if (!this.fire.active) return 0;
    let danger = 0;
    for (const b of this.fire.burning) {
      const d = Math.hypot(b.x - x, b.z - z);
      if (d < 600) danger = Math.max(danger, b.intensity * (1 - d / 600));
    }
    return danger;
  }
}
export { SCAR_CELL };
